import { parse, isValid, isFuture, differenceInYears } from 'date-fns';

function isEmpty(value) {
  return value === undefined || value === null || value === '' || Array.isArray(value) && value.length === 0 || typeof value === 'object' && Object.keys(value).length === 0;
}
const isFunction = value => !isEmpty(value) && typeof value === 'function';
// All the error messages are not real and we need to find a way to pass error messages from the outside
/**
 * Composes field validators into one, returning the first error message any of
 * them produces.
 *
 * The value is `any` rather than `unknown` deliberately, at both ends. Almost
 * every validator below narrows to the type its own field holds (`string`,
 * `Record<string, string>`, …), and under `strictFunctionTypes` a
 * `(value: string) => …` is not assignable to a `(value: unknown) => …` — so
 * passing those validators in errored. Returning `unknown` errored in turn at
 * every call site, because a `validate` prop wants an error message back.
 */
function composeValidators(...validators) {
  return value => {
    return validators.reduce((error, validator) => error || validator(value), undefined);
  };
}
function isEmail(value) {
  if (isEmpty(value) || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]*[^.]([a-zA-Z0-9.-]*[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/i.test(value)) {
    return 'Invalid Email Address';
  }
}
function isEmailIfExist(value) {
  if (!isEmpty(value) && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]*[^.]([a-zA-Z0-9.-]*[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/i.test(value)) {
    return 'Invalid Email Address';
  }
}
function isRequired(value) {
  if (isEmpty(value)) {
    return 'This field is required.';
  }
}
function isChecked(value) {
  if (typeof value === 'boolean' && value) {
    return;
  }
  return 'Unchecked.';
}
/** Use it when we need to overwrite the default required message */
function isRequiredWithCustomMessage(message) {
  return params => {
    return isRequired(params) ? message : undefined;
  };
}
function isPhone(value) {
  // stolen from: https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s02.html
  const phoneRegex = /^\d{10}$/;
  if (isEmpty(value)) {
    return;
  }
  if (!phoneRegex.test(value)) {
    return 'Please check that the phone number you entered is\u00a0correct.';
  }
}
function minLength(len) {
  return value => {
    if (isEmpty(value)) {
      return;
    }
    if (isEmpty(value) || value?.length < len) {
      return 'Invalid string.';
    }
  };
}
function maxLength(len) {
  return value => {
    if (value.length > len) {
      return 'Invalid string.';
    }
  };
}
const isValidAddress = value => {
  if (isEmpty(value.city)) {
    return 'Invalid.';
  } else if (isEmpty(value.streetAddress)) {
    return 'Invalid.';
  } else if (isEmpty(value.zipcode)) {
    return 'Invalid.';
  }
};
function isInteger(value) {
  if (!isEmpty(value) && !Number.isInteger(Number(value))) {
    return 'Must be a whole number.';
  }
}
function isCreditCard(value) {
  if (isEmpty(value)) {
    return 'Credit card number is required.';
  }
  if (isInteger(value)) {
    return 'Credit card number must contain only digits.';
  }
  const isAmex = /^34/.test(value) || /^37/.test(value);
  const creditCardNumberLength = isAmex ? 15 : 16;
  if (value.length !== creditCardNumberLength) {
    return isAmex ? 'American Express cards must have 15 digits.' : 'Credit card number must have 16 digits.';
  }
}
// `creditCardNumber` is optional because the form calls this while the card
// number is still empty. `?? ''` below preserves what that did: `RegExp.test`
// coerced the absent number to `'undefined'`, so the Amex branch was already
// false for it.
function isCreditCardCVC(value, creditCardNumber) {
  if (isEmpty(value)) {
    return 'Security code (CVC) is required.';
  }
  if (isInteger(value)) {
    return 'Security code must contain only digits.';
  }
  const cardNumber = creditCardNumber ?? '';
  const isAmex = /^34/.test(cardNumber) || /^37/.test(cardNumber);
  const cvcLength = isAmex ? 4 : 3;
  if (value.trim().length !== cvcLength) {
    return isAmex ? 'American Express security code must be 4 digits.' : 'Security code (CVC) must be 3 digits.';
  }
}
function isDateMMDDYY(value) {
  if (isEmpty(value)) {
    return 'Verify the date format.';
  }
  const splittedValue = value.split('/');
  if (value.split('/').length === 3) {
    const [mm, dd, yy] = splittedValue;
    if (yy?.length === 2) {
      const dateObj = parse(`20${yy}/${mm}/${dd}`, 'yyyy/MM/dd', new Date());
      if (!isValid(dateObj)) {
        return 'Verify the date format.';
      }
    } else {
      return 'Verify the date format.';
    }
  } else {
    return 'Verify the date format.';
  }
}
function isFutureDateMMDDYY(value) {
  const [mm, dd, yy] = value.split('/');
  const dateObj = parse(`20${yy}/${mm}/${dd}`, 'yyyy/MM/dd', new Date());
  if (!isFuture(dateObj)) {
    return 'The date needs to be in the future.';
  }
}
function isDateMMYY(value) {
  if (isEmpty(value)) {
    return 'Expiration date is required.';
  }
  const splittedValue = value.split('/');
  if (splittedValue.length !== 2) {
    return 'Invalid expiration date format (MM/YY).';
  }
  const [mm, yy] = splittedValue;
  if (yy?.length !== 2) {
    return 'Invalid expiration date format (MM/YY).';
  }
  const month = Number(mm);
  if (month < 1 || month > 12) {
    return 'Invalid month. Please enter a value between 01-12.';
  }
  const dateObj = parse(`20${yy}/${mm}/01`, 'yyyy/MM/dd', new Date());
  if (!isValid(dateObj)) {
    return 'Invalid expiration date format (MM/YY).';
  }
}
function isDateMMYYYY(value) {
  if (isEmpty(value)) {
    return 'Verify the date format.';
  }
  const splittedValue = value.split('/');
  if (value.split('/').length === 2) {
    const [mm, yyyy] = splittedValue;
    if (yyyy?.length === 4) {
      const dateObj = parse(`${yyyy}/${mm}/01`, 'yyyy/MM/dd', new Date());
      if (!isValid(dateObj)) {
        return 'Verify the date format.';
      }
    } else {
      return 'Verify the date format.';
    }
  } else {
    return 'Verify the date format.';
  }
}
const isFutureDateMMYY = value => {
  const [mm, yy] = value.split('/');
  const dateObj = parse(`20${yy}/${mm}/01`, 'yyyy/MM/dd', new Date());
  if (!isFuture(dateObj)) {
    return 'Card has expired. Please use a valid expiration date.';
  }
};
function isPastDateMMDDYY(value) {
  const dateObj = parse(value, 'MM/dd/yy', new Date());
  if (isFuture(dateObj)) {
    return 'The date needs to be in the past.';
  }
}
function isPastDateYYYY(value) {
  const dateObj = parse(value, 'yyyy', new Date());
  if (isFuture(dateObj)) {
    return 'The date needs to be in the past.';
  }
}
function isZipCode(value) {
  if (isEmpty(value)) {
    return 'Billing ZIP code is required.';
  }
  if (isInteger(value)) {
    return 'ZIP code must contain only digits.';
  }
  if (value.length !== 5) {
    return 'ZIP code must be 5 digits.';
  }
}
function isGreaterThan(comparisonValue) {
  return value => {
    if (!Number.isNaN(value)) {
      if (Number(value) <= comparisonValue) {
        return 'The value is lower than expected.';
      }
    }
  };
}
function isSmallerThan(comparisonValue) {
  return value => {
    if (!Number.isNaN(value)) {
      if (Number(value) >= comparisonValue) {
        return 'The value is higher than expected.';
      }
    }
  };
}
const dateValidationMMYY = value => {
  if (!isEmpty(value) && value.toString().split('/').length === 2) {
    const [mm, yy] = value.toString().split('/');
    if (yy?.length === 2) {
      const dateObj = new Date(`20${yy}/${mm}/01`);
      if (!isValid(dateObj)) {
        return 'Verify the date format.';
      }
    } else {
      return 'Verify the date format.';
    }
  } else {
    return 'Verify the date format.';
  }
};
function isFutureDateMMDDYYYY(value) {
  const dateObj = parse(value, 'MM/dd/yyyy', new Date());
  if (!isFuture(dateObj)) {
    return 'The date needs to be in the future.';
  }
}
function isPastDateMMDDYYYY(value) {
  const dateObj = parse(value, 'MM/dd/yyyy', new Date());
  if (isFuture(dateObj)) {
    return 'The date needs to be in the past.';
  }
}
function isKid(value) {
  const dateObj = parse(value, 'MM/dd/yyyy', new Date());
  const age = differenceInYears(new Date(), dateObj);
  if (age >= 18) {
    return 'Verify the date format.';
  }
}
function isSSN(value) {
  const ssnRegex = /^\d{9}$/;
  if (!ssnRegex.test(value)) {
    return 'Invalid SSN';
  }
}
function isSSNWithLastDigits(value) {
  if (Boolean(value) && !/^[0-9]{3}-?[0-9]{2}-?[0-9]{4}$/i.test(value) && !/^XXXXX-?[0-9]{4}$/i.test(value)) {
    return 'Invalid SSN';
  }
}
function isValidUrl(value) {
  if (value && !/^(https?:\/\/)([\w.-]+)\.([a-z]{2,})(:[0-9]{1,5})?(\/[^\s]*)?$/i.test(value)) {
    return 'Invalid URL';
  }
}
function isVerificationCode(value, length = 6) {
  if (value.length !== length) {
    return 'Invalid verification code';
  }
}

export { composeValidators, dateValidationMMYY, isChecked, isCreditCard, isCreditCardCVC, isDateMMDDYY, isDateMMYY, isDateMMYYYY, isEmail, isEmailIfExist, isEmpty, isFunction, isFutureDateMMDDYY, isFutureDateMMDDYYYY, isFutureDateMMYY, isGreaterThan, isInteger, isKid, isPastDateMMDDYY, isPastDateMMDDYYYY, isPastDateYYYY, isPhone, isRequired, isRequiredWithCustomMessage, isSSN, isSSNWithLastDigits, isSmallerThan, isValidAddress, isValidUrl, isVerificationCode, isZipCode, maxLength, minLength };
